import {
    type NavigationEntry,
    navigationEntrySchema,
    navigationPathname,
    type SlotAssignmentsResponse,
    slotIdSchema,
    widgetSchema,
} from '@luminovo/apps-types';
import { z } from 'zod';

// `navigation` is the app's declared tree, verbatim; optional because absence
// is meaningful (the host derives the rail from the routes instead). The
// platform validated it against the app's routes at upload; this host checks its
// shape here, and that it stays under /apps (`withoutNavigationOutsideApps`).
const assignedAppSchema = z
    .object({
        id: z.string().min(1),
        name: z.string().min(1),
        description: z.string(),
        navigation: navigationEntrySchema.optional(),
    })
    .strict();

const parseUrl = (value: string): URL | undefined => {
    try {
        return new URL(value);
    } catch {
        return undefined;
    }
};

// `__Host-apps_sess` carries the Secure attribute, so the apps host has to be a
// secure origin or the browser drops the cookie and every widget renders the 401
// page. Loopback counts as secure, which is what makes a locally served apps host
// usable in development.
const LOOPBACK_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]']);

const isSecureOrigin = (url: URL): boolean =>
    url.protocol === 'https:' || (url.protocol === 'http:' && LOOPBACK_HOSTNAMES.has(url.hostname));

// An ORIGIN and nothing else: no path, no query, no fragment, no trailing slash.
// Comparing `URL.origin` back to the input is what enforces that — `origin`
// serialises exactly the parts we allow, so any extra character fails. This
// matters beyond tidiness: the value is compared byte-for-byte against an
// incoming `event.origin` and used as a postMessage `targetOrigin`, and neither
// comparison tolerates a stray slash.
const appsOriginSchema = z.string().refine(
    (value) => {
        const parsed = parseUrl(value);
        return parsed !== undefined && parsed.origin === value && isSecureOrigin(parsed);
    },
    { message: 'Expected the apps host as a bare secure origin' },
);

// Absolute, and pinned to the apps origin by the response-level check below. A
// query string is allowed — a bundle URL may carry a cache-busting version — but
// credentials and a fragment are not: neither means anything to an iframe `src`,
// and `URL.origin` ignores userinfo, so a credentialed URL would otherwise pass
// the origin check while pointing the frame somewhere we did not intend.
const bundleUrlSchema = z.string().refine(
    (value) => {
        const parsed = parseUrl(value);
        return parsed !== undefined && !parsed.username && !parsed.password && !parsed.hash;
    },
    { message: 'Expected an absolute widget URL without credentials or a fragment' },
);

// The literal placeholder the SDK substitutes with a workflow id. Its presence is
// checked rather than assumed: a template without it would send every workflow of
// an app to the same URL.
//
// Checked against the RAW string, never a parsed URL — `new URL()` percent-encodes
// the braces (`%7BworkflowId%7D`), which is also why nothing on either side of the
// bridge may round-trip this value through the URL parser.
const WORKFLOW_ID_PLACEHOLDER = '{workflowId}';

// Absolute and placeholder-bearing, pinned to the apps origin by the response-level
// check below. The origin matters more sharply here than for `bundle_url`: a widget
// POSTs its workflow input to this URL, so a template on another host would send
// that input off-origin (and arrive without the cookie that authorizes it). Drift
// has to fail here rather than inside a widget.
const workflowRunUrlTemplateSchema = z.string().refine(
    (value) => {
        const parsed = parseUrl(value);
        return (
            parsed !== undefined &&
            !parsed.username &&
            !parsed.password &&
            !parsed.hash &&
            value.includes(WORKFLOW_ID_PLACEHOLDER)
        );
    },
    { message: `Expected an absolute workflow run URL containing ${WORKFLOW_ID_PLACEHOLDER}` },
);

// The base a declared bundle-relative path (a navigation icon's
// `assets/catalog/<file>`) is appended to. Absolute, a bare path with a trailing
// slash and nothing else — no query, fragment or credentials — so concatenation
// is the only operation it ever needs. Pinned to the apps origin below, like
// every other URL the host acts on.
const assetsBaseUrlSchema = z.string().refine(
    (value) => {
        const parsed = parseUrl(value);
        return (
            parsed !== undefined &&
            !parsed.username &&
            !parsed.password &&
            !parsed.search &&
            !parsed.hash &&
            parsed.pathname.endsWith('/') &&
            value === `${parsed.origin}${parsed.pathname}`
        );
    },
    { message: 'Expected an absolute assets base URL ending in a slash' },
);

// Every platform-derived URL field is optional on the wire, and they are absent
// together: a host with no derived apps host publishes none of them. `bundle_path`
// is always present, superseded, and accepted here only so a response does not fail
// `.strict()` — nothing reads it.
const assignmentSchema = z
    .object({
        app: assignedAppSchema,
        widget: widgetSchema,
        bundle_path: z.string(),
        bundle_url: bundleUrlSchema.optional(),
        workflow_run_url_template: workflowRunUrlTemplateSchema.optional(),
        assets_base_url: assetsBaseUrlSchema.optional(),
    })
    .strict();

// Names the entry in a warning without trusting its shape.
const describeEntry = (entry: unknown): string => {
    const view = entry as { app?: { id?: unknown }; widget?: { id?: unknown } } | null;
    const app = typeof view?.app?.id === 'string' ? view.app.id : '<unknown app>';
    const widget = typeof view?.widget?.id === 'string' ? view.widget.id : '<unknown widget>';
    return `${app}/${widget}`;
};

const isUnderApps = (path: string): boolean => {
    const pathname = navigationPathname(path);
    return pathname === '/apps' || pathname.startsWith('/apps/');
};

/** The first path in the tree, in declaration order, that lies outside /apps. */
const pathOutsideApps = (entry: NavigationEntry): string | undefined => {
    if (!isUnderApps(entry.path)) {
        return entry.path;
    }
    for (const child of entry.children) {
        const path = pathOutsideApps(child);
        if (path !== undefined) {
            return path;
        }
    }
    return undefined;
};

type ParsedAssignment = z.infer<typeof assignmentSchema>;

// A navigation leaving /apps (stored before the upload check, or drift) is dropped: the routes derive the rail.
// The platform checks at upload that an app's /apps routes cover each path; the rail links them via the /apps splat.
function withoutNavigationOutsideApps(assignments: ParsedAssignment[]): ParsedAssignment[] {
    // App id to its first path outside /apps. An app's navigation repeats on each of its widgets.
    const ignored = new Map<string, string>();
    const kept = assignments.map((assignment) => {
        const { navigation, ...app } = assignment.app;
        const stray = navigation === undefined ? undefined : pathOutsideApps(navigation);
        if (stray === undefined) {
            return assignment;
        }
        ignored.set(app.id, stray);
        return { ...assignment, app };
    });
    if (ignored.size > 0) {
        const apps = [...ignored].map(([id, path]) => `${id} (${path})`);
        console.warn(
            `[apps] ignored the navigation of ${apps.length} app(s) with a path outside /apps: ${apps.join(', ')}.`,
        );
    }
    return kept;
}

// A widget declaration this host cannot render is dropped; everything else still
// faces the strict schema below.
//
// Otherwise one app stored under rules newer than this bundle would fail the array and blank the slot and palette.
//
// The URL and origin checks stay loud, because a bundle URL on the wrong host is
// drift worth failing on rather than quietly hiding: they are checked on the
// entries that survive, by the strict schema and the response-level refinement.
const assignmentsSchema = z
    .array(z.unknown())
    .transform((entries) => {
        const renderable = entries.filter(
            (entry) => widgetSchema.safeParse((entry as { widget?: unknown } | null)?.widget).success,
        );
        if (renderable.length !== entries.length) {
            const skipped = entries.filter((entry) => !renderable.includes(entry)).map(describeEntry);
            console.warn(`[apps] skipped ${skipped.length} widget(s) this host cannot render: ${skipped.join(', ')}.`);
        }
        return renderable;
    })
    .pipe(z.array(assignmentSchema))
    .transform(withoutNavigationOutsideApps);

export const SlotAssignmentsResponseSchema: z.ZodType<SlotAssignmentsResponse, unknown> = z
    .object({
        slot: slotIdSchema,
        apps_origin: appsOriginSchema.optional(),
        assignments: assignmentsSchema,
    })
    .strict()
    // Cross-field, so neither check can live on its own field: every URL we act on
    // has to be on the one origin we are about to mint a session for and pin the
    // message bridge to. A bundle URL on any other host would load a document we
    // have no session with and whose messages we would then ignore anyway; a run URL
    // on another host would take a widget's input somewhere we never authorized. A
    // URL with no origin to check it against is drift too — the platform emits these
    // together or not at all.
    .superRefine((response, ctx) => {
        for (const [index, assignment] of response.assignments.entries()) {
            if (
                assignment.bundle_url !== undefined &&
                parseUrl(assignment.bundle_url)?.origin !== response.apps_origin
            ) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['assignments', index, 'bundle_url'],
                    message: `Expected a widget URL on ${response.apps_origin ?? '<missing apps_origin>'}`,
                });
            }
            // Checked independently of `bundle_url` rather than in its `if`: the two
            // travel together, but a response that broke that pairing should report
            // exactly which field is wrong.
            if (
                assignment.workflow_run_url_template !== undefined &&
                parseUrl(assignment.workflow_run_url_template)?.origin !== response.apps_origin
            ) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['assignments', index, 'workflow_run_url_template'],
                    message: `Expected a workflow run URL on ${response.apps_origin ?? '<missing apps_origin>'}`,
                });
            }
            // An icon loads as an <img> from wherever this points, so an
            // off-origin base is the same drift as an off-origin bundle URL.
            if (
                assignment.assets_base_url !== undefined &&
                parseUrl(assignment.assets_base_url)?.origin !== response.apps_origin
            ) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['assignments', index, 'assets_base_url'],
                    message: `Expected an assets base URL on ${response.apps_origin ?? '<missing apps_origin>'}`,
                });
            }
        }
    });
